import { Backdrop, Box, Button, Fade, FormControl, FormHelperText, Grid, Modal, Typography } from "@material-ui/core";
import { observer } from "mobx-react";
import React, { ChangeEvent, useState } from "react";
import { authModalStyles } from "./AuthModalStyles";
import { ReactComponent as CloseIcon } from "icons/CloseIcon.svg";
import clsx from "clsx";
import { AuthModalProps } from "./AuthModalProps";
import { LynxInput } from "components/LynxInput/LynxInput";
import { LynxLink } from "components/LynxLink/LynxLink";

export default observer(function AuthModal(props: AuthModalProps) {
    const classes = authModalStyles();
    const [email, setEmail] = useState(localStorage.getItem("email") || "");
    const [password, setPassword] = useState("");
    const [passwordHasError, setPasswordHasError] = useState(false);
    const [emailHasError, setEmailHasError] = useState(false);
    const emailRegexp =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

    const handleEmailChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setEmailHasError(false);
        setEmail(value);
    };

    const handlePasswordChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
        setPasswordHasError(false);
        setPassword(value);
    };

    const forgetPassword = (event: any) => {
        event.preventDefault();
    };

    const onClose = () => {
        if (props.onClose) {
            props.onClose();
        } else {
            console.log("No method for close button");
        }

        setPassword("");
    };

    const onCancel = () => {
        if (props.onCancel) {
            props.onCancel();
        } else {
            console.log("No method for cancel button");
        }

        setPassword("");
    };

    const isFormValid = (): boolean => {
        if ((password || password !== "") && emailRegexp.test(email)) {
            setPasswordHasError(false);
            setEmailHasError(false);
            return true;
        }

        if (!password || password === "") {
            setPasswordHasError(true);
        }

        if (!emailRegexp.test(email)) {
            setEmailHasError(true);
        }

        return false;
    };

    const onSubmit = () => {
        if (!isFormValid()) {
            return;
        }

        if (props.onSubmit) {
            props.onSubmit({ login: email, password });
        } else {
            console.log("No method for submit button");
        }
    };

    return (
        <Modal
            className={classes.modal}
            open={props.isOpen}
            onClose={onClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={props.isOpen}>
                <>
                    <Grid className={clsx(classes.paper, classes.mainGridAuth)} container direction="column">
                        <CloseIcon className={classes.icon} onClick={onClose} />
                        <Grid className={classes.firstGrid} container direction="row" justifyContent="space-between">
                            <Grid item>
                                <Typography variant="h5">Re-authentication Required</Typography>
                                <Typography className={classes.header}>
                                    In order to confirm this decision you must provide login credentials
                                </Typography>
                            </Grid>

                            <form className={classes.form}>
                                <Grid direction="row">
                                    <Grid item>
                                        <FormControl
                                            className={classes.formControl}
                                            error={emailHasError}
                                            variant="standard"
                                        >
                                            <LynxInput
                                                size="large"
                                                name="email"
                                                value={email}
                                                label="Email"
                                                type="email"
                                                onChange={handleEmailChange}
                                                required={true}
                                                error={emailHasError}
                                                placeholder="contact@carrier.com"
                                                formControlClassName={classes.form}
                                            />
                                            <FormHelperText
                                                className={emailHasError ? classes.formErrorYes : classes.formErrorNo}
                                                id="email-error-text"
                                            >
                                                Email is incorrect
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    <Grid item>
                                        <FormControl
                                            className={classes.form}
                                            error={passwordHasError}
                                            variant="standard"
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    onSubmit();
                                                }
                                            }}
                                        >
                                            <LynxInput
                                                size="large"
                                                name="password"
                                                value={password}
                                                label="Password"
                                                onChange={handlePasswordChange}
                                                type="password"
                                                required={true}
                                                error={passwordHasError}
                                                formControlClassName={classes.form}
                                            />
                                            <FormHelperText
                                                className={
                                                    passwordHasError ? classes.formErrorYes : classes.formErrorNo
                                                }
                                                id="password-error-text"
                                            >
                                                Password required
                                            </FormHelperText>
                                        </FormControl>
                                    </Grid>
                                    <Grid item>
                                        <Box display={props.errorMessage ? "block" : "none"}>
                                            <div className={classes.errorMessage}>{props.errorMessage}</div>
                                        </Box>
                                    </Grid>
                                    <Grid item>
                                        <LynxLink href="#" onClick={forgetPassword} containerClassName={classes.link}>
                                            Forgot your password?
                                        </LynxLink>
                                    </Grid>
                                    <Grid container direction="row" justifyContent="space-between">
                                        <Grid item></Grid>
                                        <Grid item className={classes.gridModalButtons} justifyContent="space-between">
                                            <Button onClick={onCancel} className={clsx(classes.button, classes.cancel)}>
                                                Cancel
                                            </Button>
                                            <Button
                                                onClick={onSubmit}
                                                className={clsx(classes.button, classes.confirm)}
                                            >
                                                Sign In
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </form>
                        </Grid>
                    </Grid>
                </>
            </Fade>
        </Modal>
    );
});
